import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, PencilSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import ExportButtons from "@/components/ExportButtons";
import ImportDialog from "@/components/ImportDialog";
import { exportToExcel, exportToPDF } from "@/lib/export";
import { useAuth } from "@/contexts/AuthContext";

const FIELD_ROLES = ["asisten_supervisor", "korsub", "korwil"];

const EXPORT_COLS = [
  { header: "Kode", accessor: (r) => r.kode },
  { header: "Nama", accessor: (r) => r.nama },
  { header: "Luas (m2)", accessor: (r) => r.luas_m2 },
  { header: "Blok", accessor: (r) => r.blok },
  { header: "Wilayah", accessor: (r) => r.wilayah },
  { header: "Pemilik", accessor: (r) => r.pemilik_nama || "-" },
  { header: "Status", accessor: (r) => r.status },
];

export default function TambakPage() {
  const { user } = useAuth();
  const isFieldRole = FIELD_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ kode: "", nama: "", luas_m2: 0, lokasi: "", blok: "", wilayah: "", pemilik_nama: "", status: "aktif" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tambak?page=${page}&limit=15&search=${search}`);
      setItems(data.data);
      setTotal(data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await api.put(`/tambak/${editItem._id}`, form);
        toast.success("Tambak berhasil diperbarui");
      } else {
        await api.post("/tambak", form);
        toast.success("Tambak berhasil ditambahkan");
      }
      setDialogOpen(false);
      setEditItem(null);
      setForm({ kode: "", nama: "", luas_m2: 0, lokasi: "", blok: "", wilayah: "", pemilik_nama: "", status: "aktif" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setForm({ kode: item.kode, nama: item.nama, luas_m2: item.luas_m2, lokasi: item.lokasi, blok: item.blok, wilayah: item.wilayah, pemilik_nama: item.pemilik_nama || "", status: item.status });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus tambak ini?")) return;
    try { await api.delete(`/tambak/${id}`); toast.success("Tambak dihapus"); load(); } catch (err) { toast.error(err.response?.data?.detail || "Gagal menghapus"); }
  };

  const statusColor = { aktif: "bg-emerald-100 text-emerald-700", nonaktif: "bg-slate-100 text-slate-500", perbaikan: "bg-amber-100 text-amber-700" };

  return (
    <div data-testid="tambak-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Manajemen Tambak</h1>
          <p className="text-sm text-slate-500 mt-1">{isFieldRole ? "Data kolam tambak udang" : "Kelola data kolam tambak udang"}</p>
        </div>
        {!isFieldRole && (
        <div className="flex flex-wrap items-center gap-2">
          <ImportDialog onSuccess={load} />
          <ExportButtons onExcel={() => exportToExcel(items, EXPORT_COLS, "data_tambak")} onPDF={() => exportToPDF(items, EXPORT_COLS, "data_tambak", "Data Tambak P3UW")} />
          <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditItem(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-tambak-btn" className="bg-[#008080] hover:bg-[#006B6B] text-white"><Plus size={18} className="mr-2" /> Tambah Tambak</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>{editItem ? "Edit Tambak" : "Tambak Baru"}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Kode</Label><Input data-testid="input-kode-tambak" value={form.kode} onChange={e => setForm({ ...form, kode: e.target.value })} required /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Nama</Label><Input data-testid="input-nama-tambak" value={form.nama} onChange={e => setForm({ ...form, nama: e.target.value })} required /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Luas (m2)</Label><Input data-testid="input-luas-tambak" type="number" step="0.1" value={form.luas_m2} onChange={e => setForm({ ...form, luas_m2: parseFloat(e.target.value) || 0 })} /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Lokasi</Label><Input data-testid="input-lokasi-tambak" value={form.lokasi} onChange={e => setForm({ ...form, lokasi: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Blok</Label><Input data-testid="input-blok-tambak" value={form.blok} onChange={e => setForm({ ...form, blok: e.target.value })} /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Wilayah</Label><Input data-testid="input-wilayah-tambak" value={form.wilayah} onChange={e => setForm({ ...form, wilayah: e.target.value })} /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="select-status-tambak"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="aktif">Aktif</SelectItem><SelectItem value="nonaktif">Nonaktif</SelectItem><SelectItem value="perbaikan">Perbaikan</SelectItem></SelectContent>
                    </Select></div>
                </div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Pemilik</Label><Input data-testid="input-pemilik-tambak" value={form.pemilik_nama} onChange={e => setForm({ ...form, pemilik_nama: e.target.value })} /></div>
                <Button type="submit" data-testid="submit-tambak-btn" className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white">Simpan</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        )}
      </div>

      <div className="relative w-full sm:w-72">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="search-tambak" placeholder="Cari tambak..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" />
      </div>

      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Kode</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Nama</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Luas</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Blok</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Wilayah</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Pemilik</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
                {!isFieldRole && <TableHead className="text-xs uppercase tracking-wider font-semibold">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={isFieldRole ? 7 : 8} className="text-center py-8 text-slate-400">Memuat...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={isFieldRole ? 7 : 8} className="text-center py-8 text-slate-400">Belum ada tambak</TableCell></TableRow>
              ) : items.map(t => (
                <TableRow key={t._id} className="hover:bg-slate-50">
                  <TableCell className="text-sm font-mono">{t.kode}</TableCell>
                  <TableCell className="text-sm font-medium">{t.nama}</TableCell>
                  <TableCell className="text-sm">{t.luas_m2} m&sup2;</TableCell>
                  <TableCell className="text-sm">{t.blok}</TableCell>
                  <TableCell className="text-sm">{t.wilayah}</TableCell>
                  <TableCell className="text-sm">{t.pemilik_nama || "-"}</TableCell>
                  <TableCell><Badge className={`${statusColor[t.status] || ""} hover:${statusColor[t.status] || ""}`}>{t.status}</Badge></TableCell>
                  {!isFieldRole && (
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(t)}><PencilSimple size={16} /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(t._id)}><Trash size={16} /></Button>
                    </div>
                  </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-4 pb-3"><Pagination page={page} limit={15} total={total} onPageChange={setPage} /></div>
        </CardContent>
      </Card>
    </div>
  );
}
